// Tuning panel for the face. Open the app with ?face=demo.
//
// Pins the face to any state or emotion, cycles through all of them, plays the
// everyday situations of a restaurant floor - "out of stock", "serving", "path
// blocked" - in English, Hindi or Bengali, and checks speech sync without a live
// call, either with a fake voice (loudness only) or with a real recording played
// through the same audio tap a call uses. A situation's face is picked by the
// same classifier that reads her live replies, so what you see here is what
// guests see.

import React, { useEffect, useRef, useState } from "react";
import { faceStore } from "../engine/faceStore";
import { classify } from "../engine/emotion";
import { SITUATIONS, LANGUAGES } from "../engine/situations";
import { startSpeakingDetector, stopSpeakingDetector } from "../../../../services/dograhHandler";
import { KNOWN_STATES, KNOWN_EMOTIONS } from "./params";

const VOICE_CLIP = "/face-demo/voice.wav";
const MS_PER_CHAR = 65; // roughly how long she takes to say a line

const PANEL = {
	position: "fixed",
	left: 12,
	right: 12,
	bottom: 12,
	zIndex: 2000,
	display: "flex",
	flexDirection: "column",
	gap: 7,
	padding: "10px 12px",
	borderRadius: 12,
	background: "rgba(20, 24, 33, 0.9)",
	backdropFilter: "blur(6px)",
	font: "600 13px/1 system-ui, sans-serif",
	color: "#cfd6e4",
};
const ROW = { display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" };
const BTN = (active, tone) => ({
	padding: "7px 10px",
	borderRadius: 8,
	border: "1px solid " + (active ? "#7fe3ff" : tone === "sit" ? "#3b3350" : "#2c3446"),
	background: active ? "rgba(127, 227, 255, 0.16)" : tone === "emo" ? "#1d2130" : tone === "sit" ? "#221d2e" : "#171b25",
	color: active ? "#ffffff" : "#aeb7c8",
	cursor: "pointer",
	font: "inherit",
});
const LABEL = { color: "#6f7a8f", minWidth: 64 };
const SAID = { font: "500 14px/1.35 system-ui, sans-serif", color: "#e8ecf4", paddingLeft: 70 };

export default function FaceDemo() {
	const [pin, setPin] = useState({ state: "idle", emotion: null });
	const [cycling, setCycling] = useState(false);
	const [talking, setTalking] = useState(false);
	const [voice, setVoice] = useState(false);
	const [lang, setLang] = useState("en");
	const [said, setSaid] = useState(null); // the situation line she is saying
	const talkRef = useRef(false);
	const sayTimer = useRef(0);

	// Everything to walk through: each state bare, then each emotion over idle.
	const sequence = useRef([
		...KNOWN_STATES.map((state) => ({ state, emotion: null })),
		...KNOWN_EMOTIONS.map((emotion) => ({ state: "idle", emotion })),
	]).current;

	useEffect(() => {
		faceStore.setOverride(pin);
	}, [pin]);

	useEffect(
		() => () => {
			clearTimeout(sayTimer.current);
			faceStore.setOverride(null);
		},
		[],
	);

	useEffect(() => {
		if (!cycling) return undefined;
		let i = Math.max(0, sequence.findIndex((s) => s.state === pin.state && s.emotion === pin.emotion));
		const id = setInterval(() => {
			i = (i + 1) % sequence.length;
			setPin(sequence[i]);
		}, 2800);
		return () => clearInterval(id);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cycling]);

	// A fake voice: bursts of syllables with pauses, fed as real amplitude.
	useEffect(() => {
		talkRef.current = talking;
		if (!talking) return undefined;
		let raf;
		const t0 = performance.now();
		const tick = () => {
			if (!talkRef.current) return;
			const t = (performance.now() - t0) / 1000;
			const phrase = Math.sin(t * 1.3) > -0.55 ? 1 : 0; // occasional pauses
			const syllable = Math.abs(Math.sin(t * 11)) * (0.55 + 0.45 * Math.abs(Math.sin(t * 3.1)));
			faceStore.setAmplitude(phrase * syllable);
			raf = requestAnimationFrame(tick);
		};
		raf = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(raf);
	}, [talking]);

	// A real voice: the clip plays through an <audio> element that the speaking
	// detector taps, exactly as it taps the call's audio.
	useEffect(() => {
		if (!voice) return undefined;
		const el = document.createElement("audio");
		el.src = VOICE_CLIP;
		el.style.display = "none";
		el.onended = () => setVoice(false);
		document.body.appendChild(el);
		startSpeakingDetector((speaking, level, shape) => faceStore.setAmplitude(speaking ? level : 0, shape));
		el.play().catch(() => setVoice(false));
		return () => {
			stopSpeakingDetector();
			el.pause();
			el.remove();
			faceStore.setAmplitude(0);
		};
	}, [voice]);

	const choose = (next) => {
		clearTimeout(sayTimer.current);
		setSaid(null);
		setCycling(false);
		setPin(next);
	};

	// She "says" the situation's line: the face comes from classifying the line,
	// exactly as it would from her live reply, she talks for about as long as
	// the line takes, and then keeps the face for a moment after.
	const say = (s) => {
		const text = s[lang];
		const c = classify(text);
		const face = c ? c.emotion : null;
		clearTimeout(sayTimer.current);
		setCycling(false);
		setVoice(false);
		setSaid({ text, face, reason: c ? c.reason : "no feeling found" });
		setPin({ state: "speaking", emotion: face });
		setTalking(true);
		sayTimer.current = setTimeout(() => {
			setTalking(false);
			setPin({ state: "idle", emotion: face });
		}, Math.max(1800, text.length * MS_PER_CHAR));
	};

	return (
		<div style={PANEL} data-face-demo="">
			<div style={ROW}>
				<span style={LABEL}>state</span>
				{KNOWN_STATES.map((s) => (
					<button key={s} style={BTN(pin.state === s && !pin.emotion)} onClick={() => choose({ state: s, emotion: null })}>
						{s}
					</button>
				))}
			</div>
			<div style={ROW}>
				<span style={LABEL}>emotion</span>
				{KNOWN_EMOTIONS.map((e) => (
					<button key={e} style={BTN(pin.emotion === e, "emo")} onClick={() => choose({ state: pin.state === "asleep" ? "idle" : pin.state, emotion: e })}>
						{e}
					</button>
				))}
			</div>
			<div style={ROW}>
				<span style={LABEL}>situation</span>
				{SITUATIONS.map((s) => (
					<button key={s.id} style={BTN(said && said.text === s[lang], "sit")} onClick={() => say(s)}>
						{s.label}
					</button>
				))}
			</div>
			<div style={ROW}>
				<span style={LABEL}>voice</span>
				{LANGUAGES.map(([code, name]) => (
					<button key={code} style={BTN(lang === code)} onClick={() => setLang(code)}>
						{name}
					</button>
				))}
				<button style={BTN(cycling)} onClick={() => setCycling((c) => !c)}>
					{cycling ? "stop cycle" : "cycle all"}
				</button>
				{/* The "made by Aeonix" card she shows every few minutes while resting (Brand.js). */}
				<button style={BTN(false)} onClick={() => window.dispatchEvent(new Event("misti:brand"))}>
					brand
				</button>
				<button
					style={BTN(talking)}
					onClick={() => {
						setTalking((v) => !v);
						if (!talking) setPin((p) => ({ ...p, state: "speaking" }));
					}}
				>
					{talking ? "stop talking" : "fake voice"}
				</button>
				<button
					style={BTN(voice)}
					onClick={() => {
						setTalking(false);
						setVoice((v) => !v);
						if (!voice) setPin((p) => ({ ...p, state: "speaking" }));
					}}
				>
					{voice ? "stop voice" : "real voice"}
				</button>
				<span style={{ marginLeft: "auto", color: "#7fe3ff" }}>
					{pin.state}
					{pin.emotion ? ` + ${pin.emotion}` : ""}
				</span>
			</div>
			{said && (
				<div style={SAID}>
					“{said.text}” <span style={{ color: "#7fe3ff" }}>→ {said.face || "no emotion"}</span>
					<span style={{ color: "#6f7a8f" }}> ({said.reason})</span>
				</div>
			)}
		</div>
	);
}
